import { useEffect, useRef, useState } from 'react'
import { Save, Loader2, Check } from 'lucide-react'
import { useBlocker, useLocation } from 'react-router-dom'
import { SECTIONS } from '@/data/sections.data'
import { SectionCard } from '@/components/form/SectionCard'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { NewInspectionConflictDialog } from '@/components/ui/NewInspectionConflictDialog'
import { getMessage } from '@/utils/messages'
import { PageLoader } from '@/components/ui/LoadingSpinner'
import { useValuationStore } from '@/store/valuationStore'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { useTourUiStore } from '@/store/tourStore'
import { useSaveDraft, useSubmitValuation, useDeleteClaim } from '@/hooks/useValuationMutations'
import { useSubmitValidation, useSaveValidation, type SectionValidationError } from '@/hooks/useFormValidation'
import { useRefData } from '@/hooks/useRefData'
import { cn } from '@/utils/cn'
import { REF_KEYS } from '@/data'
import { getBaseCategoriesForSection, normaliseToKey } from '@/data/refCategories'
import { getClaimByUserIdAndCaseId } from '@/services/claimService'
import { loadClaimForEditing } from '@/utils/loadClaimForEditing'
import { FormRecognizer } from '@/components/form/FormRecognizer'
import { clearFormRecognizerPhotos } from '@/utils/clearFormRecognizerPhotos'
import { log } from '@/utils/logger'

export function FormPage() {
  const { activeHelpField, clearApiFieldErrors } = useUiStore()
  const { status, formValues, isDirty, toggleSection, setHasTriedSubmit, setHasTriedSave, setIsDirty } = useValuationStore()
  const saveDraft = useSaveDraft()
  const submitValuation = useSubmitValuation()
  const deleteClaim = useDeleteClaim()
  const validateForSubmit = useSubmitValidation()
  const validateForSave = useSaveValidation()
  const { loadBatch, isLoading: isRefLoading } = useRefData()

  const location = useLocation()
  const isSubmitted = status === 'submitted'
  const { isTourActive } = useTourUiStore()

  // While the guided tour is driving navigation (e.g. Previous/Next between steps), treat it
  // as intentional rather than accidental — otherwise the tour gets stuck behind this dialog.
  const blocker = useBlocker(isDirty && !isSubmitted && !isTourActive)

  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [validationErrors, setValidationErrors] = useState<SectionValidationError[]>([])
  const [showNewInspectionConfirm, setShowNewInspectionConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<{ userId: string; caseId: string } | null>(null)
  const [isCheckingClaim, setIsCheckingClaim] = useState(false)
  const isSubmittingFlow = useRef(false)

  const vehicleType = (formValues['basic-information'] as Record<string, string>)?.['vehicleType'] ?? ''
  const make = (formValues['standard-equipment'] as Record<string, string>)?.['make'] ?? ''

  // On mount: load claim from navigation state (Edit) or mark hydrated for New Inspection
  useEffect(() => {
    const state = location.state as { userId?: string; caseId?: string } | null
    const { user } = useAuthStore.getState()
    const userId = state?.userId ?? String(user?.userId ?? '')
    const caseId = state?.caseId

    if (caseId) {
      let cancelled = false
      setIsCheckingClaim(true)
      getClaimByUserIdAndCaseId(userId, caseId).then(async (res) => {
        if (!cancelled && res.data) {
          await loadClaimForEditing(userId, res.data)
        }
      }).catch(() => {}).finally(() => {
        if (!cancelled) setIsCheckingClaim(false)
      })
      return () => { cancelled = true }
    } else {
      // Dashboard already called resetForm() before navigating here
      useValuationStore.getState().markHydrated()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch basic-information API-driven dropdowns on mount
  useEffect(() => {
    log.info('FormPage mounted', { page: 'FormPage' })
    loadBatch([REF_KEYS.TYPE_OF_LOSS, REF_KEYS.VEHICLE_TYPE, REF_KEYS.COUNTRY_STATE, REF_KEYS.SECTION])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Eagerly pre-load standard-equipment base ref categories whenever vehicleType
  // is known — ensures ref IDs can be resolved for saving even if the section
  // hasn't been opened yet (e.g. loading an existing draft and saving immediately).
  useEffect(() => {
    if (!vehicleType || vehicleType === 'Select') return
    const cats = getBaseCategoriesForSection('standard-equipment', vehicleType)
    if (cats.length) loadBatch(cats)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType])

  // Pre-load the make-dependent model category whenever make is set.
  // When make is "Not in List" use the shared NOT_IN_LIST_MODEL category so the
  // model field can resolve a real ref_master ID rather than a hardcoded string.
  useEffect(() => {
    if (!make || make === 'Select' || !vehicleType || vehicleType === 'Select') return
    if (make.toLowerCase() === 'not in list') {
      loadBatch([REF_KEYS.NOT_IN_LIST_MODEL])
    } else {
      const prefix = vehicleType === 'Truck/Tractor' ? 'TRUCK' : 'TRAILER'
      loadBatch([`${prefix}_MODEL_${normaliseToKey(make)}`])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [make, vehicleType])

  // Open first section on mount
  useEffect(() => {
    const timer = setTimeout(() => toggleSection(SECTIONS[0].id), 80)
    return () => clearTimeout(timer)
  }, [toggleSection])

// Warn before browser/tab/close/refresh when there are unsaved changes.
  // Custom messages are not supported in modern browsers — the native dialog is shown.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && !isSubmitted) {
        e.preventDefault()
        // Required by Chrome/Edge — without this they silently skip the dialog
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty, isSubmitted])

  const handleSaveClick = () => {
    const errors = validateForSave()
    if (errors.length > 0) {
      log.warn('saveDraft blocked — field validation errors', {
        sectionCount: String(errors.length),
        sections: errors.map((e) => e.section).join(','),
      })
      setHasTriedSave(true)
    } else {
      setHasTriedSave(false)
      saveDraft.mutate(undefined)
    }
  }

  const handleSubmitClick = () => {
    setHasTriedSubmit(true)
    const errors = validateForSubmit()
    if (errors.length > 0) {
      log.warn('submitValuation blocked — validation errors', {
        sectionCount: String(errors.length),
        sections: errors.map((e) => e.section).join(','),
      })
      setValidationErrors(errors)
    } else {
      log.info('submitValuation confirmed — showing confirm dialog')
      setValidationErrors([])
      setShowSubmitConfirm(true)
    }
  }

  const handleConfirmSubmit = () => {
    log.info('submitValuation user confirmed')
    setShowSubmitConfirm(false)
    isSubmittingFlow.current = true
    saveDraft.mutate(
      { silent: true },
      {
        onSuccess: () => submitValuation.mutate(),
        onSettled: () => { isSubmittingFlow.current = false },
      }
    )
  }

  const handleNewInspectionClick = async () => {

    if (isSubmitted) {
      setValidationErrors([])
      clearApiFieldErrors()
      useValuationStore.getState().resetForm()
      loadBatch([REF_KEYS.TYPE_OF_LOSS, REF_KEYS.VEHICLE_TYPE, REF_KEYS.COUNTRY_STATE, REF_KEYS.SECTION])
      setTimeout(() => toggleSection(SECTIONS[0].id), 80)
      return
    }
    const userId = String(useAuthStore.getState().user?.userId ?? '')
    const caseId = useValuationStore.getState().caseId ?? ''
    log.info('newInspection requested', { hasUserId: String(!!userId), hasCaseId: String(!!caseId) })
    if (!userId || !caseId) {
      setShowNewInspectionConfirm(true)
      return
    }
    setIsCheckingClaim(true)
    try {
      const result = await getClaimByUserIdAndCaseId(userId, caseId)
      if (result.success) {
        log.info('newInspection — existing claim found, prompting delete', { userId, caseId })
        setPendingDelete({ userId, caseId })
        setShowDeleteConfirm(true)
      } else {
        setShowNewInspectionConfirm(true)
      }
    } catch {
      setShowNewInspectionConfirm(true)
    } finally {
      setIsCheckingClaim(false)
    }
  }

  const handleStartNewEmpty = () => {
    log.info('newInspection — starting empty inspection, existing preserved', {
      userId: pendingDelete?.userId ?? '',
      caseId: pendingDelete?.caseId ?? '',
    })
    setShowDeleteConfirm(false)
    setPendingDelete(null)
    setValidationErrors([])
    clearApiFieldErrors()
    useValuationStore.getState().clearLocalDraft()
    loadBatch([REF_KEYS.TYPE_OF_LOSS, REF_KEYS.VEHICLE_TYPE, REF_KEYS.COUNTRY_STATE, REF_KEYS.SECTION])
    setTimeout(() => toggleSection(SECTIONS[0].id), 80)
  }

  const handleConfirmDelete = () => {
    log.info('deleteClaim user confirmed', { userId: pendingDelete?.userId ?? '', caseId: pendingDelete?.caseId ?? '' })
    setShowDeleteConfirm(false)
    setValidationErrors([])
    clearApiFieldErrors()
    useValuationStore.getState().resetForm()
    loadBatch([REF_KEYS.TYPE_OF_LOSS, REF_KEYS.VEHICLE_TYPE, REF_KEYS.COUNTRY_STATE, REF_KEYS.SECTION])
    deleteClaim.mutate(pendingDelete!, {
      onSuccess: () => {
        log.info('deleteClaim complete — form reset', { userId: pendingDelete?.userId ?? '', caseId: pendingDelete?.caseId ?? '' })
        setPendingDelete(null)
        setTimeout(() => toggleSection(SECTIONS[0].id), 80)
      },
    })
  }

  const isPageLoading = isRefLoading || isCheckingClaim || saveDraft.isPending || submitValuation.isPending || deleteClaim.isPending

  return (
    <main
      className={cn(
        'flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-7',
        'transition-[padding-right] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
        activeHelpField && 'md:pr-[calc(var(--help-w)+36px)]'
      )}
    >
      {isPageLoading && (
        <PageLoader
          label={
            isCheckingClaim ? getMessage('form_page.loading_checking')
            : isSubmittingFlow.current || submitValuation.isPending ? getMessage('form_page.loading_submitting')
            : saveDraft.isPending ? getMessage('form_page.loading_saving')
            : deleteClaim.isPending ? getMessage('form_page.loading_deleting')
            : getMessage('form_page.loading_default')
          }
        />
      )}

      {/* Page header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1
            className="text-[22px] sm:text-[28px] leading-[1.1] text-[var(--text)]"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {getMessage('form_page.title')}
          </h1>
        </div>

        {/* Top-right actions */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={handleNewInspectionClick}
            className={cn(
              'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] font-semibold',
              'hover:border-[var(--accent)] hover:text-[var(--accent)]',
              'hover:bg-[rgba(61,142,240,0.05)] transition-all duration-[180ms]'
            )}
          >
            {getMessage('form_page.button_new_inspection')}
          </button>

        </div>
      </div>

      {/* Section cards */}
      {/* mb clears the stacked fixed bars: mobile nav (60px) + Save/Submit bar (~70px) + breathing room */}
      <div className="mb-[160px] md:mb-[100px]">
        <FormRecognizer />
        {SECTIONS.filter((s) => s.id !== 'valuation-report').map((section, i) => (
          <SectionCard key={section.id} section={section} index={i} />
        ))}
      </div>

      {/* Sticky action bar */}
      <div
        className={cn(
          'fixed bar-bottom left-0 right-0 z-[200]',
          'bg-white/95 border-t border-[var(--border)] backdrop-blur-[12px]',
          'px-4 sm:px-8 py-4 flex items-center justify-between gap-4',
          activeHelpField && 'md:pr-[calc(var(--help-w)+32px)]'
        )}
        style={{ transition: 'padding-right 0.28s cubic-bezier(0.4,0,0.2,1)' }}
      >
        <span className="text-[11px] text-[var(--text-muted)]" />

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-tour="save-draft-button"
            onClick={handleSaveClick}
            disabled={saveDraft.isPending || isSubmitted}
            className={cn(
              'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] cursor-pointer',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-[180ms]',
              (saveDraft.isPending || isSubmitted) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {saveDraft.isPending
              ? <><Loader2 size={13} strokeWidth={2} className="animate-spin" />{getMessage('form_page.button_saving')}</>
              : <><Save size={13} strokeWidth={2} />{getMessage('form_page.button_save_draft')}</>
            }
          </button>

          <button
            type="button"
            data-tour="submit-button"
            onClick={handleSubmitClick}
            disabled={submitValuation.isPending || isSubmitted}
            className={cn(
              'flex items-center gap-2 px-5 py-[9px] rounded-[var(--radius-sm)]',
              'text-white text-[12px] font-medium',
              'transition-all duration-[180ms] border border-transparent',
              isSubmitted ? 'cursor-default' : 'hover:opacity-90 cursor-pointer',
              submitValuation.isPending && 'cursor-not-allowed opacity-70'
            )}
            style={{
              background: isSubmitted
                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                : 'linear-gradient(135deg, var(--accent), #2563eb)',
            }}
          >
            {submitValuation.isPending
              ? <><Loader2 size={13} strokeWidth={2} className="animate-spin" />{getMessage('form_page.button_processing')}</>
              : isSubmitted
                ? <><Check size={13} strokeWidth={2.5} />{getMessage('form_page.button_submitted')}</>
                : <><Check size={13} strokeWidth={2} />{getMessage('form_page.button_submit')}</>
            }
          </button>
        </div>
      </div>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}

      {/* New Inspection confirmation */}
      <ConfirmDialog
        open={showNewInspectionConfirm}
        title={getMessage('inspection.confirm.new_title')}
        message={getMessage('inspection.confirm.new_body')}
        confirmLabel={getMessage('inspection.confirm.new_confirm')}
        cancelLabel={getMessage('common.button.cancel')}
        onConfirm={() => {
          setShowNewInspectionConfirm(false)
          setValidationErrors([])
          clearApiFieldErrors()
          useValuationStore.getState().resetForm()
          loadBatch([REF_KEYS.TYPE_OF_LOSS, REF_KEYS.VEHICLE_TYPE, REF_KEYS.COUNTRY_STATE, REF_KEYS.SECTION])
        }}
        onCancel={() => setShowNewInspectionConfirm(false)}
      />

      {/* Existing inspection conflict — start empty (preserve existing) or delete and start new */}
      <NewInspectionConflictDialog
        open={showDeleteConfirm}
        loading={deleteClaim.isPending}
        onStartEmpty={handleStartNewEmpty}
        onDeleteAndStartNew={handleConfirmDelete}
        onCancel={() => { setShowDeleteConfirm(false); setPendingDelete(null) }}
      />

      {/* Validation error dialog — blocks submission until fields are filled */}
      <ConfirmDialog
        open={validationErrors.length > 0}
        title={`⚠ ${getMessage('valuation.confirm.validation_title')}`}
        message={
          <div className="space-y-3">
            <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
              {getMessage('valuation.confirm.validation_intro')}
            </p>
            <div className="space-y-3 max-h-[240px] overflow-y-auto pr-1">
              {validationErrors.map(({ section, fields }) => (
                <div key={section}>
                  <p className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-[0.6px] mb-1">
                    {section}
                  </p>
                  <ul className="space-y-[3px]">
                    {fields.map(f => (
                      <li key={f} className="flex items-center gap-[6px] text-[11.5px] text-[var(--error)]">
                        <span className="w-[5px] h-[5px] rounded-full bg-[var(--error)] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        }
        confirmLabel={getMessage('valuation.confirm.validation_confirm')}
        hideCancel
        onConfirm={() => setValidationErrors([])}
        onCancel={() => setValidationErrors([])}
      />


      {/* Submit confirmation — only shown when all required fields are filled */}
      <ConfirmDialog
        open={showSubmitConfirm}
        title={getMessage('valuation.confirm.submit_title')}
        message={getMessage('valuation.confirm.submit_body')}
        confirmLabel={getMessage('valuation.confirm.submit_confirm')}
        cancelLabel={getMessage('valuation.confirm.submit_cancel')}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowSubmitConfirm(false)}
      />

      {/* Navigation block — warns when leaving with unsaved changes */}
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        title={getMessage('form_page.unsaved_changes_title')}
        message={getMessage('form_page.unsaved_changes_message')}
        confirmLabel={getMessage('form_page.button_leave_anyway')}
        cancelLabel={getMessage('form_page.button_stay')}
        onConfirm={() => {
          setIsDirty(false)
          clearFormRecognizerPhotos()
          blocker.proceed?.()
        }}
        onCancel={() => blocker.reset?.()}
      />
    </main>
  )
}
